// src/components/mitchRoom/modelOutputs/PlayerResultsGrid.js
// Season × gameweek matrix: one row per player, one cell per fixture.
// Each cell: xPts vs actual points, xMins vs actual minutes, and P(start)/
// P(play) with a tick/cross for what actually happened. Future cells show
// the projections on a soft purple wash. Rows are sorted by the period sum
// of actual points (falling back to xPts where no actual exists).
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { cn } from 'lib/utils';
import { getPlayerResultsGrid } from '../../../services/api';
import LoadingState from '../../shared/LoadingState';
import ExplanationModal from './ExplanationModal';
import FilterSelect from './FilterSelect';
import { getLogo, getPositionLabel } from './utils';
import { abbreviate, oppLabel, pct01, pointsClasses } from './gridUtils';

/** Case- and diacritic-insensitive match for the player search box. */
const normalize = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

const PLAYER_COL = 170; // sticky player column width (px)
const TOTAL_COL = 48; // each sticky totals column width (px)
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 200];

const POSITION_OPTIONS = [
  { id: 1, label: 'GKP' },
  { id: 2, label: 'DEF' },
  { id: 3, label: 'MID' },
  { id: 4, label: 'FWD' },
];

function outcomeMark(actual) {
  if (actual === 1) return <span className="text-emerald-600">✓</span>;
  if (actual === 0) return <span className="text-neutral-300">✕</span>;
  return null;
}

function cellTitle(cell, gw, playerName, oppName) {
  const ha = cell.home_away === 'H' ? 'vs' : '@';
  const lines = [`GW${gw} · ${playerName} ${ha} ${oppName} · ${cell.kickoff_datetime || ''}`];
  const xp = cell.expected_points != null ? cell.expected_points.toFixed(2) : '–';
  if (cell.is_future) {
    lines.push(`xPts ${xp} · xMins ${Math.round(cell.expected_minutes ?? 0)}`);
    lines.push(`P(start) ${pct01(cell.prob_starting)}% · P(play) ${pct01(cell.prob_playing)}%`);
    lines.push('Upcoming fixture');
  } else {
    lines.push(`xPts ${xp} → ${cell.actual_points ?? '–'} pts`);
    lines.push(
      `xMins ${Math.round(cell.expected_minutes ?? 0)} → ${
        cell.actual_minutes != null ? Math.round(cell.actual_minutes) : '–'
      } mins`
    );
    lines.push(
      `P(start) ${pct01(cell.prob_starting)}% (${cell.actual_started === 1 ? 'started' : 'did not start'}) · ` +
        `P(play) ${pct01(cell.prob_playing)}% (${cell.actual_played === 1 ? 'played' : 'did not play'})`
    );
  }
  if (cell.is_injured === 1) lines.push('Flagged injured for this fixture (model input)');
  return lines.join('\n');
}

function PlayerFixtureBox({ cell, oppShort }) {
  const injured = cell.is_injured === 1;
  // Opponent uses the FDR casing convention: HUL = home fixture, hul = away
  const opp = oppLabel(oppShort, cell.home_away);

  if (cell.is_future) {
    return (
      <div
        className={cn(
          'flex w-full flex-col items-center gap-px rounded-token-sm border border-brand-purple1/20 bg-brand-purple1/5 px-1 py-0.5',
          injured && 'border-l-2 border-l-destructive'
        )}
      >
        <span className="flex items-center gap-1 text-[9px] leading-tight tabular-nums text-neutral-600">
          <span className="font-medium text-neutral-700">{opp}</span>
          {cell.expected_minutes != null ? `${Math.round(cell.expected_minutes)}'` : '–'}
        </span>
        <span className="text-token-sm font-semibold leading-tight tabular-nums">
          {cell.expected_points != null ? cell.expected_points.toFixed(1) : '–'}
        </span>
        <span className="text-[9px] leading-tight tabular-nums text-neutral-500">
          S {pct01(cell.prob_starting)} · P {pct01(cell.prob_playing)}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center gap-px rounded-token-sm px-1 py-0.5',
        injured && 'border-l-2 border-l-destructive'
      )}
    >
      <span className="flex items-center gap-1 text-[9px] leading-tight tabular-nums text-neutral-600">
        <span className="font-medium text-neutral-700">{opp}</span>
        {cell.expected_minutes != null ? Math.round(cell.expected_minutes) : '–'}'/
        {cell.actual_minutes != null ? Math.round(cell.actual_minutes) : '–'}'
      </span>
      <span className="flex items-center gap-1 leading-tight">
        <span className="text-[10px] tabular-nums text-neutral-500">
          {cell.expected_points != null ? cell.expected_points.toFixed(1) : '–'}
        </span>
        {cell.actual_points != null && (
          <span
            className={cn(
              'rounded-[3px] px-1 text-[10px] font-semibold leading-[14px] tabular-nums',
              pointsClasses(cell.actual_points)
            )}
          >
            {cell.actual_points}
          </span>
        )}
      </span>
      <span className="text-[9px] leading-tight tabular-nums text-neutral-500">
        S {pct01(cell.prob_starting)}
        {outcomeMark(cell.actual_started)} · P {pct01(cell.prob_playing)}
        {outcomeMark(cell.actual_played)}
      </span>
    </div>
  );
}

export default function PlayerResultsGrid({ teamMap, elementMap, refreshKey }) {
  const [data, setData] = useState(null);
  const [season, setSeason] = useState(null); // null = server default (newest)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const [gwStart, setGwStart] = useState(null); // null = season start/end
  const [gwEnd, setGwEnd] = useState(null);
  const [teamFilter, setTeamFilter] = useState(null);
  const [positionFilter, setPositionFilter] = useState(null);
  const [nameQuery, setNameQuery] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPlayerResultsGrid(season)
      .then((resp) => {
        if (cancelled) return;
        setData(resp);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching player results grid:', err);
        setError(err.message || 'Failed to load player results grid');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [season, refreshKey, attempt]);

  // Reset paging whenever a filter or the underlying data changes the row set
  useEffect(() => {
    setPage(0);
  }, [data, gwStart, gwEnd, teamFilter, positionFilter, nameQuery, rowsPerPage]);

  const seasonOptions = useMemo(
    () => (data?.seasons || []).map((s) => ({ id: s.compSeasonID, label: s.label })),
    [data?.seasons]
  );

  const gwOptions = useMemo(
    () => (data?.gameweeks || []).map((gw) => ({ id: gw, label: `GW${gw}` })),
    [data?.gameweeks]
  );

  const teamOptions = useMemo(() => {
    if (!data) return [];
    const seen = new Map();
    data.rows.forEach((row) => {
      if (row.club_optaID && !seen.has(row.club_optaID)) {
        const name = teamMap?.get(row.club_optaID)?.name || row.club || row.club_optaID;
        seen.set(row.club_optaID, name);
      }
    });
    return [...seen.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data, teamMap]);

  // Effective GW window (defaults to full season, clamped to available GWs)
  const gws = useMemo(() => data?.gameweeks || [], [data?.gameweeks]);
  const startSel = gwStart != null && gws.includes(gwStart) ? gwStart : gws[0];
  const endSel = gwEnd != null && gws.includes(gwEnd) ? gwEnd : gws[gws.length - 1];
  // Tolerate a reversed From/To selection by normalising the window
  const effStart = Math.min(startSel, endSel);
  const effEnd = Math.max(startSel, endSel);
  const visibleGws = useMemo(
    () => gws.filter((gw) => gw >= effStart && gw <= effEnd),
    [gws, effStart, effEnd]
  );

  const processedRows = useMemo(() => {
    if (!data) return [];
    const query = normalize(nameQuery.trim());
    return data.rows
      .filter((row) => !teamFilter || row.club_optaID === teamFilter)
      .filter((row) => !positionFilter || row.element_type === positionFilter)
      .map((row) => {
        let totalX = 0;
        let totalPts = 0;
        let sortKey = 0;
        let cellCount = 0;
        let hasX = false;
        let hasPts = false;
        visibleGws.forEach((gw) => {
          const list = row.cells[String(gw)];
          if (!list) return;
          list.forEach((cell) => {
            cellCount += 1;
            if (cell.expected_points != null) {
              totalX += cell.expected_points;
              hasX = true;
            }
            if (cell.actual_points != null) {
              totalPts += cell.actual_points;
              hasPts = true;
            }
            // Period sort: actual points where the game is played, xPts otherwise
            sortKey += cell.actual_points != null ? cell.actual_points : (cell.expected_points ?? 0);
          });
        });
        const name =
          elementMap?.get(row.player_optaID)?.web_name || row.player_name || row.player_optaID;
        return {
          ...row,
          name,
          totalX: hasX ? totalX : null,
          totalPts: hasPts ? totalPts : null,
          sortKey,
          cellCount,
        };
      })
      .filter((row) => row.cellCount > 0)
      .filter(
        (row) =>
          !query ||
          normalize(row.name).includes(query) ||
          normalize(row.player_name).includes(query)
      )
      .sort((a, b) => b.sortKey - a.sortKey || a.name.localeCompare(b.name));
  }, [data, teamFilter, positionFilter, nameQuery, visibleGws, elementMap]);

  const pageCount = Math.max(1, Math.ceil(processedRows.length / rowsPerPage));
  const pagedRows = processedRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  if (loading && !data) {
    return <LoadingState message="Loading player results grid..." minHeight={200} />;
  }

  if (error) {
    return (
      <Alert variant="destructive" className="max-w-xl">
        <AlertTitle>Error loading player results</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={() => setAttempt((a) => a + 1)}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!data || data.rows.length === 0) {
    return (
      <div className="p-6 text-center text-token-md text-muted-foreground">
        No player predictions available.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <Label className="text-token-base font-normal text-muted-foreground">Player</Label>
          <Input
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            placeholder="Search player..."
            aria-label="Search player"
            className="h-9 w-[180px]"
          />
        </div>
        <FilterSelect
          label="Season"
          value={season ?? data.compSeasonID}
          options={seasonOptions}
          onChange={(v) => {
            setSeason(v);
            setGwStart(null);
            setGwEnd(null);
          }}
          className="h-9 w-[140px]"
        />
        <FilterSelect
          label="From"
          value={effStart}
          options={gwOptions}
          onChange={(v) => setGwStart(v)}
          className="h-9 w-[110px]"
        />
        <FilterSelect
          label="To"
          value={effEnd}
          options={gwOptions}
          onChange={(v) => setGwEnd(v)}
          className="h-9 w-[110px]"
        />
        <FilterSelect
          label="Team"
          allLabel="All Teams"
          value={teamFilter}
          options={teamOptions}
          onChange={setTeamFilter}
          className="h-9 w-[180px]"
        />
        <FilterSelect
          label="Position"
          allLabel="All Positions"
          value={positionFilter}
          options={POSITION_OPTIONS}
          onChange={setPositionFilter}
          className="h-9 w-[140px]"
        />
        {/* Legend — literal copies of the real chips */}
        <div className="ml-auto flex flex-wrap items-center gap-3 text-token-base text-neutral-500">
          <span className="flex items-center gap-1">
            <span className="text-[10px] tabular-nums text-neutral-500">2.9</span>
            <span
              className={cn(
                'rounded-[3px] px-1 text-[10px] font-semibold leading-[14px]',
                pointsClasses(7)
              )}
            >
              7
            </span>
            xPts → actual
          </span>
          <span>
            S/P % <span className="text-emerald-600">✓</span> did ·{' '}
            <span className="text-neutral-300">✕</span> didn't
          </span>
          <span>
            <span className="font-medium text-neutral-700">HUL</span> home ·{' '}
            <span className="font-medium text-neutral-700">hul</span> away
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded-[2px] border border-brand-purple1/20 bg-brand-purple1/5" />
            upcoming
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-1 rounded-[1px] bg-destructive" />
            flagged injured
          </span>
        </div>
      </div>

      <p className="mb-2 text-token-base text-muted-foreground">
        {processedRows.length} players · GW{effStart}–GW{effEnd} · sorted by points over the
        window (actual where played, xPts otherwise) · click a cell for its explanation (component breakdown or SHAP)
      </p>

      <div
        className={cn(
          'max-h-[70dvh] min-h-[280px] overflow-auto rounded-token-large border border-borderGrey bg-white',
          loading && 'opacity-60'
        )}
      >
        <table className="w-full border-separate border-spacing-0 text-token-base">
          <thead>
            <tr>
              <th
                className="sticky left-0 top-0 z-[4] border-b border-borderGrey bg-white p-2 text-left font-semibold"
                style={{ minWidth: PLAYER_COL, width: PLAYER_COL }}
              >
                Player
              </th>
              <th
                className="sticky top-0 z-[4] border-b border-borderGrey bg-white px-1 py-2 text-center font-semibold"
                style={{ left: PLAYER_COL, minWidth: TOTAL_COL, width: TOTAL_COL }}
                title="Sum of expected points over the selected gameweeks"
              >
                xPts
              </th>
              <th
                className="sticky top-0 z-[4] border-b border-r border-borderGrey bg-white px-1 py-2 text-center font-semibold"
                style={{ left: PLAYER_COL + TOTAL_COL, minWidth: TOTAL_COL, width: TOTAL_COL }}
                title="Sum of actual FPL points over the selected gameweeks"
              >
                Pts
              </th>
              {visibleGws.map((gw) => (
                <th
                  key={gw}
                  className="sticky top-0 z-sticky-header w-[88px] min-w-[88px] border-b border-borderGrey bg-white px-0 py-1 text-center font-semibold"
                >
                  GW{gw}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row) => {
              const team = teamMap?.get(row.club_optaID);
              const clubShort = team?.short_name || abbreviate(row.club);
              const logo = getLogo(team?.name || row.club);
              return (
                <tr key={row.player_optaID} className="group border-b border-borderGrey">
                  <td
                    className="sticky left-0 z-sticky-column border-b border-borderGrey bg-white px-2 py-1 group-hover:bg-neutral-50"
                    style={{ minWidth: PLAYER_COL, width: PLAYER_COL }}
                  >
                    <div className="flex items-center gap-2">
                      {logo && (
                        <img src={logo} alt={clubShort} className="h-5 w-5 object-contain" />
                      )}
                      <div className="min-w-0">
                        <p className="truncate font-medium leading-tight">{row.name}</p>
                        <p className="truncate text-token-xs leading-tight text-muted-foreground">
                          {getPositionLabel(row.element_type)} · {clubShort}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td
                    className="sticky z-sticky-column border-b border-borderGrey bg-white px-1 py-1 text-center tabular-nums text-neutral-600 group-hover:bg-neutral-50"
                    style={{ left: PLAYER_COL, minWidth: TOTAL_COL, width: TOTAL_COL }}
                  >
                    {row.totalX != null ? row.totalX.toFixed(1) : '–'}
                  </td>
                  <td
                    className="sticky z-sticky-column border-b border-r border-borderGrey bg-white px-1 py-1 text-center font-semibold tabular-nums group-hover:bg-neutral-50"
                    style={{ left: PLAYER_COL + TOTAL_COL, minWidth: TOTAL_COL, width: TOTAL_COL }}
                  >
                    {row.totalPts != null ? row.totalPts : '–'}
                  </td>
                  {visibleGws.map((gw) => {
                    const list = row.cells[String(gw)];
                    if (!list || list.length === 0) {
                      return (
                        <td
                          key={gw}
                          className="border-b border-borderGrey bg-white px-0 py-1 text-center align-middle group-hover:bg-neutral-50"
                        >
                          <span className="select-none text-neutral-300">–</span>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={gw}
                        className="border-b border-borderGrey bg-white px-0.5 py-1 align-middle group-hover:bg-neutral-50"
                      >
                        <div className="flex flex-col gap-0.5">
                          {list.map((cell) => {
                            const oppShort =
                              teamMap?.get(cell.opp_club_optaID)?.short_name ||
                              abbreviate(cell.opp_club);
                            return (
                              <button
                                key={cell.fixture_optaID}
                                type="button"
                                className={cn(
                                  'w-full rounded-token-sm',
                                  cell.expected_points != null &&
                                    'cursor-pointer hover:ring-1 hover:ring-neutral-300'
                                )}
                                title={cellTitle(cell, gw, row.name, cell.opp_club || oppShort)}
                                onClick={() =>
                                  cell.expected_points != null &&
                                  setModal({
                                    entityId: row.player_optaID,
                                    fixtureId: cell.fixture_optaID,
                                    entityName: row.name,
                                    opponent: oppShort,
                                  })
                                }
                              >
                                <PlayerFixtureBox cell={cell} oppShort={oppShort} />
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination — simple prev/next controls */}
      <div className="mt-2 flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <span className="text-token-base text-muted-foreground">Rows per page</span>
          <Select
            value={String(rowsPerPage)}
            onValueChange={(v) => setRowsPerPage(parseInt(v, 10))}
          >
            <SelectTrigger className="h-8 w-[80px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROWS_PER_PAGE_OPTIONS.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="text-token-base text-muted-foreground">
          Page {page + 1} of {pageCount} · {processedRows.length} players
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            aria-label="Previous page"
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={page >= pageCount - 1}
            aria-label="Next page"
          >
            <ChevronRight />
          </Button>
        </div>
      </div>

      {/* Explanation Modal (v2 components or legacy SHAP) */}
      <ExplanationModal
        open={modal != null}
        onClose={() => setModal(null)}
        modelType="player"
        entityId={modal?.entityId}
        fixtureId={modal?.fixtureId}
        contextInfo={{ entityName: modal?.entityName, opponent: modal?.opponent }}
      />
    </div>
  );
}
